import cx from 'classnames';

import { Icon } from '@sb/design-system';
import type { IconKind } from '@sb/design-system';

export interface ChatMenuItemDef {
  /** Label shown to the user. Also used as the unique key. */
  label: string;
  iconKind: IconKind;
  onClick: () => void;
  disabled?: boolean;
  /**
   * When true, the icon and label render in brand orange to signal a
   * destructive action (e.g. Delete). The hover state is unchanged.
   */
  destructive?: boolean;
}

interface ChatMenuItemProps {
  item: ChatMenuItemDef;
  /**
   * Called after the item's own onClick fires. Typically used by the
   * parent popover to close itself.
   */
  onAfterClick?: () => void;
}

/**
 * Single row inside a chat-panel kebab popover. Shared between the
 * chat header's app-menu (`ChatHeader`) and per-row menus in
 * `ChatHistoryRow`. Icon-left + 13/500 label, 4%-black hover
 * background, dimmed when disabled.
 *
 * Lives in its own file so both consumers can render it without
 * importing the entire chat-header surface.
 */
export function ChatMenuItem({ item, onAfterClick }: ChatMenuItemProps) {
  const handleClick = () => {
    if (item.disabled) return;
    item.onClick();
    onAfterClick?.();
  };

  return (
    <button
      type="button"
      role="menuitem"
      onClick={handleClick}
      aria-disabled={item.disabled}
      className={cx(
        'tw-flex',
        'tw-items-center',
        'tw-gap-10',
        'tw-w-full',
        'tw-py-10',
        'tw-px-8',
        'tw-bg-transparent',
        'tw-border-none',
        'tw-text-left',
      )}
      style={{
        // Border-radius `8px` isn't in the design-system scale (which
        // jumps 6 → 10); cursor/opacity flip with `disabled` and the
        // background flips on hover via the handlers below.
        cursor: item.disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        borderRadius: 8,
        transition: 'background 0.12s ease',
        opacity: item.disabled ? 0.4 : 1,
      }}
      onMouseEnter={(e) => {
        if (item.disabled) return;
        // eslint-disable-next-line no-param-reassign -- hover background is set imperatively so it can be cleared on leave
        e.currentTarget.style.background = 'var(--fill-primary)';
      }}
      onMouseLeave={(e) => {
        // eslint-disable-next-line no-param-reassign -- pair with onMouseEnter above
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <Icon
        kind={item.iconKind}
        className="tw-icon-16 tw-shrink-0"
        style={{
          color: item.destructive ? 'var(--orange)' : 'var(--label-secondary)',
        }}
      />
      <span
        className="tw-text-13 tw-font-medium"
        style={{
          color: item.destructive ? 'var(--orange)' : 'var(--label-primary)',
        }}
      >
        {item.label}
      </span>
    </button>
  );
}
